import React, { useState, useEffect } from "react";
import { useAuth, API, formatApiErrorDetail } from "@/App";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarCheck, Clock, Briefcase, Loader2, RefreshCw, Users, CalendarDays, Pencil, XCircle, Check, X, UserPlus } from "lucide-react";
import { format, parseISO, isToday, isTomorrow } from "date-fns";

const OpenShifts = () => {
  const { user, getAuthHeaders } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickingUp, setPickingUp] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [selectedShift, setSelectedShift] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({ event_name: "", start_time: "", end_time: "" });
  const [savingEdit, setSavingEdit] = useState(false);
  const [processingId, setProcessingId] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [assignEmployeeId, setAssignEmployeeId] = useState("");
  const [assigning, setAssigning] = useState(false);

  const canManage = user?.role === "admin" || user?.role === "supervisor";

  useEffect(() => {
    fetchOpenShifts();
    if (canManage) fetchEmployees();
  }, []);

  const fetchOpenShifts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/shifts/open`, { headers: getAuthHeaders() });
      setShifts(response.data);
    } catch (error) {
      toast.error("Failed to load open shifts");
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API}/employees`, { headers: getAuthHeaders() });
      setEmployees(res.data);
    } catch (error) {
      console.error("Failed to fetch employees");
    }
  };

  const handleShiftClick = (shift) => {
    setSelectedShift(shift);
    setAssignEmployeeId("");
    setShowDetailDialog(true);
  };

  const handleConfirmPickup = async () => {
    if (!selectedShift) return;
    setPickingUp(selectedShift.id);
    try {
      await axios.post(`${API}/shift-pickup-requests`, { shift_id: selectedShift.id }, { headers: getAuthHeaders() });
      toast.success("Bid submitted successfully!");
      setShowDetailDialog(false);
      fetchOpenShifts();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to submit bid");
    } finally {
      setPickingUp(null);
    }
  };

  const handleCancelBid = async (bidId, shiftId) => {
    setCancelling(shiftId);
    try {
      await axios.delete(`${API}/shift-pickup-requests/${bidId}`, { headers: getAuthHeaders() });
      toast.success("Bid cancelled");
      setShowDetailDialog(false);
      fetchOpenShifts();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to cancel bid");
    } finally {
      setCancelling(null);
    }
  };

  const handleEditClick = (shift, e) => {
    e.stopPropagation();
    setSelectedShift(shift);
    setEditForm({
      event_name: shift.event_name,
      start_time: shift.start_time,
      end_time: shift.end_time,
    });
    setShowEditDialog(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedShift) return;
    setSavingEdit(true);
    try {
      await axios.put(`${API}/shifts/${selectedShift.id}`, editForm, { headers: getAuthHeaders() });
      toast.success("Shift updated");
      setShowEditDialog(false);
      fetchOpenShifts();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to update shift");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleApproveBid = async (bidId) => {
    setProcessingId(bidId);
    try {
      await axios.put(`${API}/shift-pickup-requests/${bidId}/approve`, {}, { headers: getAuthHeaders() });
      toast.success("Bid approved — shift assigned");
      setShowDetailDialog(false);
      fetchOpenShifts();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to approve bid");
    } finally {
      setProcessingId(null);
    }
  };

  const handleRejectBid = async (bidId) => {
    setProcessingId(bidId);
    try {
      await axios.put(`${API}/shift-pickup-requests/${bidId}/reject`, {}, { headers: getAuthHeaders() });
      toast.success("Bid rejected");
      fetchOpenShifts();
      // Update the selected shift's bidders locally
      if (selectedShift) {
        setSelectedShift(prev => ({
          ...prev,
          bidders: prev.bidders.filter(b => b.id !== bidId),
          bid_count: prev.bid_count - 1,
        }));
      }
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to reject bid");
    } finally {
      setProcessingId(null);
    }
  };

  const handleAssignEmployee = async () => {
    if (!selectedShift || !assignEmployeeId) return;
    setAssigning(true);
    try {
      await axios.post(`${API}/shifts/${selectedShift.id}/assign?employee_id=${assignEmployeeId}`, {}, { headers: getAuthHeaders() });
      toast.success("Employee assigned to shift");
      setShowDetailDialog(false);
      setAssignEmployeeId("");
      fetchOpenShifts();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || "Failed to assign employee");
    } finally {
      setAssigning(false);
    }
  };


  const formatDateLabel = (dateStr) => {
    const date = parseISO(dateStr);
    if (isToday(date)) return "Today";
    if (isTomorrow(date)) return "Tomorrow";
    return format(date, "EEEE, MMM d");
  };

  const getBidLabel = (count) => {
    if (count === 0) return "No bids yet";
    if (count === 1) return "1 person bidding";
    return `${count} people bidding`;
  };

  // Group shifts by date
  const grouped = shifts.reduce((acc, shift) => {
    if (!acc[shift.date]) acc[shift.date] = [];
    acc[shift.date].push(shift);
    return acc;
  }, {});

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="open-shifts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-heading text-[#1A1A1A]">Open Shifts</h1>
          <p className="text-[#666666]">
            {shifts.length} open shift{shifts.length !== 1 ? "s" : ""} available
          </p>
        </div>
        <Button variant="outline" onClick={fetchOpenShifts} className="border-[#E5E5E5] gap-1" data-testid="refresh-open-shifts">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#6A1E2E]" />
        </div>
      ) : shifts.length === 0 ? (
        <Card className="border border-[#E5E5E5] shadow-none">
          <CardContent className="py-16 text-center">
            <CalendarCheck className="w-12 h-12 mx-auto text-[#999999] mb-4" />
            <p className="text-[#666666] text-lg font-medium">No open shifts available</p>
            <p className="text-[#999999] text-sm mt-1">All shifts are currently assigned</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([date, dateShifts]) => (
            <div key={date}>
              <div className="flex items-center gap-2 mb-3">
                <h2 className="text-sm font-semibold text-[#6A1E2E] uppercase tracking-wide">
                  {formatDateLabel(date)}
                </h2>
                <span className="text-xs text-[#999999]">{format(parseISO(date), "MM/dd/yyyy")}</span>
                <Badge variant="outline" className="text-xs border-[#6A1E2E] text-[#6A1E2E]">
                  {dateShifts.length}
                </Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {dateShifts.map((shift) => (
                  <Card
                    key={shift.id}
                    className="border border-[#E5E5E5] shadow-none hover:border-[#6A1E2E] transition-colors cursor-pointer"
                    onClick={() => handleShiftClick(shift)}
                    data-testid={`open-shift-${shift.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <div className="flex items-start justify-between">
                          <h3 className="font-semibold text-[#1A1A1A]">{shift.event_name}</h3>
                          <div className="flex items-center gap-1">
                            {shift.my_bid_id && (
                              <Badge className="bg-green-100 text-green-800 text-xs hover:bg-green-100">
                                Your Bid
                              </Badge>
                            )}
                            {shift.bid_count > 0 && (
                              <Badge className="bg-amber-100 text-amber-800 text-xs hover:bg-amber-100" data-testid={`bid-count-${shift.id}`}>
                                <Users className="w-3 h-3 mr-1" />
                                {shift.bid_count}
                              </Badge>
                            )}
                            {canManage && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-[#6A1E2E] hover:bg-[#F0EAEB]"
                                onClick={(e) => handleEditClick(shift, e)}
                                data-testid={`edit-shift-${shift.id}`}
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-sm text-[#666666]">
                          <Clock className="w-3.5 h-3.5" />
                          {shift.start_time} - {shift.end_time}
                        </div>
                        {(shift.positions_needed?.length > 0 || shift.position_for_slot) && (
                          <div className="flex items-center gap-1 text-sm text-[#666666]">
                            <Briefcase className="w-3.5 h-3.5" />
                            {shift.position_for_slot || shift.positions_needed?.join(", ")}
                          </div>
                        )}
                        <div className="flex items-center gap-1 text-xs text-[#999999]">
                          <Users className="w-3 h-3" />
                          {getBidLabel(shift.bid_count)}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Shift Detail & Bid Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-[#1A1A1A]">
              {selectedShift?.event_name}
            </DialogTitle>
            <DialogDescription>Open shift details</DialogDescription>
          </DialogHeader>
          {selectedShift && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-xs font-medium text-[#999999] uppercase">Date</p>
                  <div className="flex items-center gap-1.5 text-sm text-[#1A1A1A]">
                    <CalendarDays className="w-4 h-4 text-[#6A1E2E]" />
                    {format(parseISO(selectedShift.date), "EEEE, MMMM d, yyyy")}
                  </div>
                </div>
                <div className="space-y-1">
                  <p className="text-xs font-medium text-[#999999] uppercase">Time</p>
                  <div className="flex items-center gap-1.5 text-sm text-[#1A1A1A]">
                    <Clock className="w-4 h-4 text-[#6A1E2E]" />
                    {selectedShift.start_time} - {selectedShift.end_time}
                  </div>
                </div>
              </div>
              {(selectedShift.positions_needed?.length > 0 || selectedShift.position_for_slot) && (
                <div className="space-y-1">
                  <p className="text-xs font-medium text-[#999999] uppercase">Position</p>
                  <div className="flex items-center gap-1.5 text-sm text-[#1A1A1A]">
                    <Briefcase className="w-4 h-4 text-[#6A1E2E]" />
                    {selectedShift.position_for_slot || selectedShift.positions_needed?.join(", ")}
                  </div>
                </div>
              )}
              <div className="p-3 rounded-sm border border-amber-200 bg-amber-50">
                <div className="flex items-center gap-2 text-sm font-medium text-amber-800">
                  <Users className="w-4 h-4" />
                  {getBidLabel(selectedShift.bid_count)}
                </div>
              </div>

              {/* Bidders list for admins */}
              {canManage && selectedShift.bidders?.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#666666]">
                    Bidders (sorted by seniority)
                  </p>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {selectedShift.bidders.map((bidder, idx) => (
                      <div
                        key={bidder.id}
                        className={`flex items-center justify-between p-2.5 rounded-sm ${
                          idx === 0 ? "bg-green-50 border border-green-200" : "bg-[#FAFAFA] border border-[#E5E5E5]"
                        }`}
                        data-testid={`bidder-${bidder.id}`}
                      >
                        <div>
                          <span className="text-sm font-medium text-[#1A1A1A]">{bidder.user_name}</span>
                          <div className="text-xs text-[#666666]">
                            Hired: {bidder.date_of_hire || "N/A"} | Week hrs: {bidder.scheduled_hours}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            onClick={() => handleApproveBid(bidder.id)}
                            disabled={processingId === bidder.id}
                            className="bg-green-600 hover:bg-green-700 text-white h-7 text-xs px-2"
                            data-testid={`approve-bid-${bidder.id}`}
                          >
                            {processingId === bidder.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" />Assign</>}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleRejectBid(bidder.id)}
                            disabled={processingId === bidder.id}
                            className="text-red-600 hover:bg-red-50 h-7 text-xs px-2"
                            data-testid={`reject-bid-${bidder.id}`}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Assign Employee directly (admin) */}
              {canManage && (
                <div className="space-y-2 pt-2 border-t border-[#E5E5E5]">
                  <p className="text-xs font-semibold uppercase tracking-wider text-[#666666]">
                    <UserPlus className="w-3.5 h-3.5 inline mr-1" />
                    Assign Employee Directly
                  </p>
                  <div className="flex gap-2">
                    <Select value={assignEmployeeId} onValueChange={setAssignEmployeeId}>
                      <SelectTrigger className="border-[#E5E5E5] flex-1" data-testid="assign-employee-select">
                        <SelectValue placeholder="Select an employee..." />
                      </SelectTrigger>
                      <SelectContent>
                        {employees.map((emp) => (
                          <SelectItem key={emp.id} value={emp.id}>
                            {emp.first_name} {emp.last_name} — {emp.role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={handleAssignEmployee}
                      disabled={!assignEmployeeId || assigning}
                      className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
                      data-testid="assign-employee-btn"
                    >
                      {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assign"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowDetailDialog(false)} className="border-[#E5E5E5]">
              Close
            </Button>
            {selectedShift?.my_bid_id ? (
              <Button
                variant="outline"
                onClick={() => handleCancelBid(selectedShift.my_bid_id, selectedShift.id)}
                disabled={cancelling === selectedShift?.id}
                className="border-red-300 text-red-700 hover:bg-red-50"
                data-testid="cancel-bid-btn"
              >
                {cancelling === selectedShift?.id ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Cancelling...</>
                ) : (
                  <><XCircle className="w-4 h-4 mr-1" /> Cancel My Bid</>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleConfirmPickup}
                disabled={pickingUp === selectedShift?.id}
                className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
                data-testid="confirm-pickup-btn"
              >
                {pickingUp === selectedShift?.id ? (
                  <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Submitting...</>
                ) : (
                  "Bid on Shift"
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Shift Dialog (Admin/Supervisor) */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#6A1E2E]" />
              Edit Shift
            </DialogTitle>
            <DialogDescription>Update shift details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-2">
              <Label>Event Name</Label>
              <Input
                value={editForm.event_name}
                onChange={(e) => setEditForm(p => ({ ...p, event_name: e.target.value }))}
                className="border-[#E5E5E5]"
                data-testid="edit-shift-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Start Time</Label>
                <Input
                  type="time"
                  value={editForm.start_time}
                  onChange={(e) => setEditForm(p => ({ ...p, start_time: e.target.value }))}
                  className="border-[#E5E5E5]"
                  data-testid="edit-shift-start"
                />
              </div>
              <div className="space-y-2">
                <Label>End Time</Label>
                <Input
                  type="time"
                  value={editForm.end_time}
                  onChange={(e) => setEditForm(p => ({ ...p, end_time: e.target.value }))}
                  className="border-[#E5E5E5]"
                  data-testid="edit-shift-end"
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowEditDialog(false)} className="border-[#E5E5E5]">Cancel</Button>
            <Button
              onClick={handleSaveEdit}
              disabled={savingEdit || !editForm.event_name}
              className="bg-[#6A1E2E] hover:bg-[#521422] text-white"
              data-testid="save-edit-shift"
            >
              {savingEdit ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OpenShifts;
